import {
  FormConfiguration,
  FormFieldType
} from '../models/forms/form-models';
import { ValidationRuleFactory } from '../services/validation-service';
import { Language } from '../constants/language';

/**
 * Event Form Configurations
 */
export const COMMON_FORM_MOCK_DATA_EN: Record<string, FormConfiguration> = {
  // Conference/Workshop Registration
  conference: {
    id: 'conference-registration',
    title: 'Conference Registration',
    description: 'Register for our upcoming spiritual conference',
    sections: [
      {
        id: 'attendee-info',
        title: 'Attendee Information',
        order: 1,
        fields: [
          {
            id: 'firstName',
            name: 'firstName',
            label: 'First Name',
            type: FormFieldType.TEXT,
            placeholder: 'Enter your first name',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'lastName',
            name: 'lastName',
            label: 'Last Name',
            type: FormFieldType.TEXT,
            placeholder: 'Enter your last name',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'Email Address',
            type: FormFieldType.EMAIL,
            placeholder: 'your.email@example.com',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.email()
            ]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'Phone Number',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.phone()
            ]
          }
        ]
      },
      {
        id: 'attendance-details',
        title: 'Attendance Details',
        order: 2,
        fields: [
          {
            id: 'attendanceType',
            name: 'attendanceType',
            label: 'Attendance Type',
            type: FormFieldType.RADIO,
            options: [
              { label: 'In-Person Attendance', value: 'in-person' },
              { label: 'Online Attendance', value: 'online' },
              { label: 'Hybrid (Some sessions online, some in-person)', value: 'hybrid' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please select attendance type')
            ]
          },
          {
            id: 'sessionsInterested',
            name: 'sessionsInterested',
            label: 'Sessions You Plan to Attend',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'Opening Keynote', value: 'opening' },
              { label: 'Worship & Prayer Sessions', value: 'worship' },
              { label: 'Healing Ministry Workshop', value: 'healing' },
              { label: 'Leadership Development', value: 'leadership' },
              { label: 'Youth Breakout Session', value: 'youth' },
              { label: 'Women\'s Ministry Session', value: 'women' },
              { label: 'Men\'s Ministry Session', value: 'men' },
              { label: 'Closing Ceremony', value: 'closing' }
            ],
            helperText: 'Select all sessions you plan to attend'
          },
          {
            id: 'dietaryRestrictions',
            name: 'dietaryRestrictions',
            label: 'Dietary Restrictions or Allergies',
            type: FormFieldType.TEXTAREA,
            placeholder: 'Please list any dietary restrictions, allergies, or special meal requirements...',
            helperText: 'This helps us prepare appropriate meals and refreshments'
          }
        ]
      },
      {
        id: 'background-interests',
        title: 'Background & Interests',
        order: 3,
        fields: [
          {
            id: 'churchBackground',
            name: 'churchBackground',
            label: 'Church Background',
            type: FormFieldType.SELECT,
            options: [
              { label: 'Select your background', value: '' },
              { label: 'New to faith', value: 'new' },
              { label: 'Growing believer', value: 'growing' },
              { label: 'Mature believer', value: 'mature' },
              { label: 'Church leader', value: 'leader' },
              { label: 'Pastor/Minister', value: 'pastor' },
              { label: 'Other', value: 'other' }
            ]
          },
          {
            id: 'expectations',
            name: 'expectations',
            label: 'What are you hoping to gain from this conference?',
            type: FormFieldType.TEXTAREA,
            placeholder: 'Share your expectations and what you hope to learn or experience...',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please share your expectations')
            ]
          },
          {
            id: 'prayerRequests',
            name: 'prayerRequests',
            label: 'Prayer Requests (Optional)',
            type: FormFieldType.TEXTAREA,
            placeholder: 'Share any prayer requests you would like our team to pray for...',
            helperText: 'Your requests will be kept confidential'
          }
        ]
      }
    ],
    submitButtonText: 'Complete Registration',
    successMessage: 'Your conference registration is complete! Check your email for confirmation details.',
    errorMessage: 'Registration failed. Please try again.'
  },

  // Retreat Registration
  retreat: {
    id: 'retreat-registration',
    title: 'Spiritual Retreat Registration',
    description: 'Join us for a transformative spiritual retreat experience',
    sections: [
      {
        id: 'participant-info',
        title: 'Participant Information',
        order: 1,
        fields: [
          {
            id: 'fullName',
            name: 'fullName',
            label: 'Full Name',
            type: FormFieldType.TEXT,
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'Email Address',
            type: FormFieldType.EMAIL,
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.email()]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'Phone Number',
            type: FormFieldType.PHONE,
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.phone()]
          },
          {
            id: 'emergencyContact',
            name: 'emergencyContact',
            label: 'Emergency Contact',
            type: FormFieldType.TEXT,
            placeholder: 'Name and phone number of emergency contact',
            required: true,
            validationRules: [ValidationRuleFactory.required('Emergency contact is required for retreats')]
          }
        ]
      },
      {
        id: 'accommodation',
        title: 'Accommodation Preferences',
        order: 2,
        fields: [
          {
            id: 'roomType',
            name: 'roomType',
            label: 'Room Preference',
            type: FormFieldType.RADIO,
            options: [
              { label: 'Single Room', value: 'single' },
              { label: 'Shared Room (2 people)', value: 'shared-2' },
              { label: 'Shared Room (3-4 people)', value: 'shared-4' },
              { label: 'Dormitory Style', value: 'dorm' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('Please select room preference')]
          },
          {
            id: 'specialAccommodations',
            name: 'specialAccommodations',
            label: 'Special Accommodation Needs',
            type: FormFieldType.TEXTAREA,
            placeholder: 'Describe any accessibility needs, medical accommodations, or special requirements...'
          },
          {
            id: 'transportation',
            name: 'transportation',
            label: 'Transportation',
            type: FormFieldType.RADIO,
            options: [
              { label: 'I will provide my own transportation', value: 'own' },
              { label: 'I need group transportation from the church', value: 'group' },
              { label: 'I need help arranging transportation', value: 'help' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('Please specify transportation needs')]
          }
        ]
      },
      {
        id: 'spiritual-preparation',
        title: 'Spiritual Preparation',
        order: 3,
        fields: [
          {
            id: 'retreatGoals',
            name: 'retreatGoals',
            label: 'What are you hoping to achieve during this retreat?',
            type: FormFieldType.TEXTAREA,
            required: true,
            validationRules: [ValidationRuleFactory.required('Please share your goals for the retreat')]
          },
          {
            id: 'spiritualChallenges',
            name: 'spiritualChallenges',
            label: 'Areas of spiritual growth you\'d like to focus on',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'Deepening prayer life', value: 'prayer' },
              { label: 'Understanding God\'s will', value: 'guidance' },
              { label: 'Healing from past wounds', value: 'healing' },
              { label: 'Strengthening faith', value: 'faith' },
              { label: 'Developing spiritual disciplines', value: 'disciplines' },
              { label: 'Overcoming fear and anxiety', value: 'fear' },
              { label: 'Finding purpose and calling', value: 'purpose' },
              { label: 'Improving relationships', value: 'relationships' }
            ]
          }
        ]
      }
    ],
    submitButtonText: 'Register for Retreat',
    successMessage: 'Your retreat registration is complete! We will send you detailed information soon.',
    errorMessage: 'Registration failed. Please try again.'
  },

  // Workshop Registration
  workshop: {
    id: 'workshop-registration',
    title: 'Workshop Registration',
    description: 'Register for our practical ministry workshop',
    sections: [
      {
        id: 'basic-info',
        title: 'Basic Information',
        order: 1,
        fields: [
          {
            id: 'fullName',
            name: 'fullName',
            label: 'Full Name',
            type: FormFieldType.TEXT,
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'Email Address',
            type: FormFieldType.EMAIL,
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.email()]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'Phone Number',
            type: FormFieldType.PHONE,
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.phone()]
          },
          {
            id: 'workshopTrack',
            name: 'workshopTrack',
            label: 'Workshop Track',
            type: FormFieldType.SELECT,
            options: [
              { label: 'Select a track', value: '' },
              { label: 'Beginner - New to Ministry', value: 'beginner' },
              { label: 'Intermediate - Some Experience', value: 'intermediate' },
              { label: 'Advanced - Leadership Level', value: 'advanced' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('Please select a workshop track')]
          }
        ]
      },
      {
        id: 'experience-goals',
        title: 'Experience & Goals',
        order: 2,
        fields: [
          {
            id: 'currentRole',
            name: 'currentRole',
            label: 'Current Ministry Role (if any)',
            type: FormFieldType.TEXT,
            placeholder: 'e.g., Youth Leader, Volunteer, Pastor, etc.'
          },
          {
            id: 'learningGoals',
            name: 'learningGoals',
            label: 'What do you hope to learn from this workshop?',
            type: FormFieldType.TEXTAREA,
            required: true,
            validationRules: [ValidationRuleFactory.required('Please share your learning goals')]
          }
        ]
      }
    ],
    submitButtonText: 'Register for Workshop',
    successMessage: 'Workshop registration complete! Check your email for materials and details.',
    errorMessage: 'Registration failed. Please try again.'
  },

  // 7 Weeks Enrollment Program
  program: {
    id: '7weeks-enrollment',
    title: '7 Weeks Harvest Program Enrollment',
    description: 'Join our transformative 7-week journey of spiritual healing and liberation',
    sections: [
      {
        id: 'personal-information',
        title: 'Personal Information',
        description: 'Tell us about yourself',
        order: 1,
        fields: [
          {
            id: 'firstName',
            name: 'firstName',
            label: 'First Name',
            type: FormFieldType.TEXT,
            placeholder: 'Enter your first name',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.minLength(2, 'First name must be at least 2 characters')
            ]
          },
          {
            id: 'lastName',
            name: 'lastName',
            label: 'Last Name',
            type: FormFieldType.TEXT,
            placeholder: 'Enter your last name',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.minLength(2, 'Last name must be at least 2 characters')
            ]
          },
          {
            id: 'email',
            name: 'email',
            label: 'Email Address',
            type: FormFieldType.EMAIL,
            placeholder: 'your.email@example.com',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.email()
            ]
          },
          {
            id: 'mobile',
            name: 'mobile',
            label: 'Mobile Number',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.phone()
            ]
          },
          {
            id: 'age',
            name: 'age',
            label: 'Age',
            type: FormFieldType.NUMBER,
            placeholder: 'Enter your age',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please enter your age'),
              ValidationRuleFactory.custom(
                (value) => value >= 18 && value <= 80,
                'Age must be between 18 and 80 years'
              )
            ]
          },
          {
            id: 'gender',
            name: 'gender',
            label: 'Gender',
            type: FormFieldType.RADIO,
            options: [
              { label: 'Male', value: 'male' },
              { label: 'Female', value: 'female' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please select your gender')
            ]
          }
        ]
      },
      {
        id: 'spiritual-background',
        title: 'Spiritual Background',
        description: 'Help us understand your Christian journey',
        order: 2,
        fields: [
          {
            id: 'christianityBackground',
            name: 'christianityBackground',
            label: 'Your Christianity Background',
            type: FormFieldType.SELECT,
            options: [
              { label: 'Select your background', value: '' },
              { label: 'New believer (less than 1 year)', value: 'new-believer' },
              { label: 'Growing Christian (1-5 years)', value: 'growing' },
              { label: 'Mature believer (5+ years)', value: 'mature' },
              { label: 'Born-again Christian from childhood', value: 'childhood' },
              { label: 'Recently returned to faith', value: 'returned' },
              { label: 'Exploring Christianity', value: 'exploring' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please select your Christianity background')
            ]
          },
          {
            id: 'churchAttendance',
            name: 'churchAttendance',
            label: 'Church Attendance',
            type: FormFieldType.RADIO,
            options: [
              { label: 'Regular attendee (weekly)', value: 'regular' },
              { label: 'Occasional attendee (monthly)', value: 'occasional' },
              { label: 'Rare attendee (special occasions)', value: 'rare' },
              { label: 'First time church experience', value: 'first-time' },
              { label: 'Online church only', value: 'online-only' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please indicate your church attendance pattern')
            ]
          },
          {
            id: 'baptismStatus',
            name: 'baptismStatus',
            label: 'Baptism Status',
            type: FormFieldType.RADIO,
            options: [
              { label: 'Yes, I have been baptized', value: 'baptized' },
              { label: 'No, but I am interested', value: 'interested' },
              { label: 'No, and I need more information', value: 'need-info' },
              { label: 'Prefer not to answer', value: 'prefer-not' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please indicate your baptism status')
            ]
          },
          {
            id: 'currentChurch',
            name: 'currentChurch',
            label: 'Current Church (if any)',
            type: FormFieldType.TEXT,
            placeholder: 'Enter your current church name',
            helperText: 'Leave blank if you don\'t currently attend a church'
          }
        ]
      },
      {
        id: 'program-specific',
        title: 'Program Information',
        description: 'Details specific to the 7 Weeks Harvest Program',
        order: 3,
        fields: [
          {
            id: 'healingAreas',
            name: 'healingAreas',
            label: 'Areas You Seek Healing or Growth',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'Emotional healing from past trauma', value: 'emotional-trauma' },
              { label: 'Spiritual bondage and freedom', value: 'spiritual-bondage' },
              { label: 'Relationship restoration', value: 'relationships' },
              { label: 'Physical healing and wellness', value: 'physical-healing' },
              { label: 'Overcoming addiction', value: 'addiction' },
              { label: 'Depression and anxiety', value: 'mental-health' },
              { label: 'Forgiveness (giving and receiving)', value: 'forgiveness' },
              { label: 'Understanding God\'s love and purpose', value: 'gods-love' },
              { label: 'Breaking generational patterns', value: 'generational' },
              { label: 'Financial breakthrough and stewardship', value: 'financial' }
            ],
            helperText: 'Select all areas that apply to your journey'
          },
          {
            id: 'commitmentLevel',
            name: 'commitmentLevel',
            label: 'Your Commitment to the 7-Week Program',
            type: FormFieldType.RADIO,
            options: [
              { label: 'I can attend all 7 sessions consistently', value: 'full-commitment' },
              { label: 'I can attend most sessions (5-6 sessions)', value: 'mostly-committed' },
              { label: 'I will try my best but may miss 2-3 sessions', value: 'partial-commitment' },
              { label: 'I can only attend occasionally', value: 'occasional' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please indicate your commitment level')
            ]
          },
          {
            id: 'motivation',
            name: 'motivation',
            label: 'What draws you to this 7-week program?',
            type: FormFieldType.TEXTAREA,
            placeholder: 'Share your heart and what you hope to gain from this transformative journey with Christ...',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Please share your motivation for joining'),
              ValidationRuleFactory.minLength(30, 'Please provide more details about your motivation (minimum 30 characters)')
            ]
          },
          {
            id: 'prayerSupport',
            name: 'prayerSupport',
            label: 'Do you have a prayer support system?',
            type: FormFieldType.RADIO,
            options: [
              { label: 'Yes, I have strong prayer support', value: 'strong-support' },
              { label: 'Yes, but limited support', value: 'limited-support' },
              { label: 'No, I need prayer support', value: 'need-support' },
              { label: 'Not sure what this means', value: 'unsure' }
            ],
            helperText: 'This helps us understand how to best support you during the program'
          }
        ]
      },
      {
        id: 'agreement-commitment',
        title: 'Program Agreement',
        description: 'Please read and confirm your understanding',
        order: 4,
        fields: [
          {
            id: 'programUnderstanding',
            name: 'programUnderstanding',
            label: 'I understand this is a 7-week intensive spiritual program that requires commitment, prayer, and openness to God\'s healing work in my life',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('You must acknowledge understanding of the program requirements')
            ]
          },
          {
            id: 'prayerConsent',
            name: 'prayerConsent',
            label: 'I consent to receive prayer ministry and understand that this program involves spiritual counseling based on Biblical principles',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Prayer ministry consent is required for participation')
            ]
          },
          {
            id: 'confidentialityAgreement',
            name: 'confidentialityAgreement',
            label: 'I agree to maintain confidentiality regarding personal testimonies and experiences shared during group sessions',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Confidentiality agreement is required')
            ]
          },
          {
            id: 'communicationConsent',
            name: 'communicationConsent',
            label: 'I consent to being contacted by ZOE Church regarding this program, follow-up care, and related spiritual growth opportunities',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('Communication consent is required')
            ]
          }
        ]
      }
    ],
    submitButtonText: 'Enroll in 7 Weeks Program',
    cancelButtonText: 'Cancel',
    successMessage: 'Praise God! Your enrollment in the 7 Weeks Harvest Program has been submitted. Our ministry team will contact you within 24 hours with next steps and preparation materials.',
    errorMessage: 'There was an error processing your enrollment. Please try again or contact our ministry office for assistance.',
    externalUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSdXkNmQfBgJHRqNzStwVkEHgQCZhJQZyPbxDctR8Sm3WqYfow/viewform?usp=header'
  }
};

// Arabic forms (simplified)
export const COMMON_FORM_MOCK_DATA_AR: Record<string, FormConfiguration> = {
  conference: {
    id: 'conference-registration',
    title: 'تسجيل المؤتمر',
    description: 'سجل في مؤتمرنا الروحي القادم',
    sections: [
      {
        id: 'attendee-info',
        title: 'معلومات الحاضر',
        order: 1,
        fields: [
          {
            id: 'firstName',
            name: 'firstName',
            label: 'الاسم الأول',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسمك الأول',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'lastName',
            name: 'lastName',
            label: 'اسم العائلة',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسم عائلتك',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'عنوان البريد الإلكتروني',
            type: FormFieldType.EMAIL,
            placeholder: 'بريدك الإلكتروني@example.com',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.email()
            ]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'رقم الهاتف',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.phone()
            ]
          }
        ]
      },
      {
        id: 'attendance-details',
        title: 'تفاصيل الحضور',
        order: 2,
        fields: [
          {
            id: 'attendanceType',
            name: 'attendanceType',
            label: 'نوع الحضور',
            type: FormFieldType.RADIO,
            options: [
              { label: 'الحضور الشخصي', value: 'in-person' },
              { label: 'الحضور عبر الإنترنت', value: 'online' },
              { label: 'مختلط (بعض الجلسات عبر الإنترنت، وبعضها شخصيًا)', value: 'hybrid' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد نوع الحضور')
            ]
          },
          {
            id: 'sessionsInterested',
            name: 'sessionsInterested',
            label: 'الجلسات التي تخطط لحضورها',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'الكلمة الافتتاحية', value: 'opening' },
              { label: 'جلسات العبادة والصلاة', value: 'worship' },
              { label: 'ورشة خدمة الشفاء', value: 'healing' },
              { label: 'تطوير القيادة', value: 'leadership' },
              { label: 'جلسة الشباب المنفصلة', value: 'youth' },
              { label: 'جلسة خدمة النساء', value: 'women' },
              { label: 'جلسة خدمة الرجال', value: 'men' },
              { label: 'حفل الختام', value: 'closing' }
            ],
            helperText: 'اختر جميع الجلسات التي تخطط لحضورها'
          },
          {
            id: 'dietaryRestrictions',
            name: 'dietaryRestrictions',
            label: 'القيود الغذائية أو الحساسية',
            type: FormFieldType.TEXTAREA,
            placeholder: 'يرجى إدراج أي قيود غذائية أو حساسية أو متطلبات وجبات خاصة...',
            helperText: 'هذا يساعدنا في إعداد الوجبات والمرطبات المناسبة'
          }
        ]
      },
      {
        id: 'background-interests',
        title: 'الخلفية والاهتمامات',
        order: 3,
        fields: [
          {
            id: 'churchBackground',
            name: 'churchBackground',
            label: 'الخلفية الكنسية',
            type: FormFieldType.SELECT,
            options: [
              { label: 'اختر خلفيتك', value: '' },
              { label: 'جديد في الإيمان', value: 'new' },
              { label: 'مؤمن نامٍ', value: 'growing' },
              { label: 'مؤمن ناضج', value: 'mature' },
              { label: 'قائد كنسي', value: 'leader' },
              { label: 'راعي/خادم', value: 'pastor' },
              { label: 'أخرى', value: 'other' }
            ]
          },
          {
            id: 'expectations',
            name: 'expectations',
            label: 'ما الذي تأمل في اكتسابه من هذا المؤتمر؟',
            type: FormFieldType.TEXTAREA,
            placeholder: 'شارك توقعاتك وما تأمل في تعلمه أو اختباره...',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى مشاركة توقعاتك')
            ]
          },
          {
            id: 'prayerRequests',
            name: 'prayerRequests',
            label: 'طلبات الصلاة (اختياري)',
            type: FormFieldType.TEXTAREA,
            placeholder: 'شارك أي طلبات صلاة تود من فريقنا أن يصلي من أجلها...',
            helperText: 'طلباتك ستبقى سرية'
          }
        ]
      }
    ],
    submitButtonText: 'إكمال التسجيل',
    successMessage: 'تم إكمال تسجيلك في المؤتمر! تحقق من بريدك الإلكتروني للحصول على تفاصيل التأكيد.',
    errorMessage: 'فشل التسجيل. يرجى المحاولة مرة أخرى.'
  },
  retreat: {
    id: 'retreat-registration',
    title: 'تسجيل الخلوة الروحية',
    description: 'انضم إلينا في تجربة خلوة روحية تحويلية',
    sections: [
      {
        id: 'participant-info',
        title: 'معلومات المشارك',
        order: 1,
        fields: [
          {
            id: 'fullName',
            name: 'fullName',
            label: 'الاسم الكامل',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسمك الكامل',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'عنوان البريد الإلكتروني',
            type: FormFieldType.EMAIL,
            placeholder: 'بريدك الإلكتروني@example.com',
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.email()]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'رقم الهاتف',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.phone()]
          },
          {
            id: 'emergencyContact',
            name: 'emergencyContact',
            label: 'جهة الاتصال الطارئة',
            type: FormFieldType.TEXT,
            placeholder: 'اسم ورقم هاتف جهة الاتصال الطارئة',
            required: true,
            validationRules: [ValidationRuleFactory.required('جهة الاتصال الطارئة مطلوبة للخلوات')]
          }
        ]
      },
      {
        id: 'accommodation',
        title: 'تفضيلات الإقامة',
        order: 2,
        fields: [
          {
            id: 'roomType',
            name: 'roomType',
            label: 'تفضيل الغرفة',
            type: FormFieldType.RADIO,
            options: [
              { label: 'غرفة فردية', value: 'single' },
              { label: 'غرفة مشتركة (شخصان)', value: 'shared-2' },
              { label: 'غرفة مشتركة (3-4 أشخاص)', value: 'shared-4' },
              { label: 'نمط المهجع', value: 'dorm' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('يرجى تحديد تفضيل الغرفة')]
          },
          {
            id: 'specialAccommodations',
            name: 'specialAccommodations',
            label: 'احتياجات الإقامة الخاصة',
            type: FormFieldType.TEXTAREA,
            placeholder: 'صف أي احتياجات إمكانية وصول أو تسهيلات طبية أو متطلبات خاصة...'
          },
          {
            id: 'transportation',
            name: 'transportation',
            label: 'المواصلات',
            type: FormFieldType.RADIO,
            options: [
              { label: 'سأوفر مواصلاتي الخاصة', value: 'own' },
              { label: 'أحتاج مواصلات جماعية من الكنيسة', value: 'group' },
              { label: 'أحتاج مساعدة في ترتيب المواصلات', value: 'help' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('يرجى تحديد احتياجات المواصلات')]
          }
        ]
      },
      {
        id: 'spiritual-preparation',
        title: 'التحضير الروحي',
        order: 3,
        fields: [
          {
            id: 'retreatGoals',
            name: 'retreatGoals',
            label: 'ما الذي تأمل في تحقيقه خلال هذه الخلوة؟',
            type: FormFieldType.TEXTAREA,
            placeholder: 'شارك أهدافك وما تأمل في اكتسابه من هذه الخلوة الروحية...',
            required: true,
            validationRules: [ValidationRuleFactory.required('يرجى مشاركة أهدافك للخلوة')]
          },
          {
            id: 'spiritualChallenges',
            name: 'spiritualChallenges',
            label: 'مجالات النمو الروحي التي تود التركيز عليها',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'تعميق حياة الصلاة', value: 'prayer' },
              { label: 'فهم إرادة الله', value: 'guidance' },
              { label: 'الشفاء من الجروح السابقة', value: 'healing' },
              { label: 'تقوية الإيمان', value: 'faith' },
              { label: 'تطوير الانضباط الروحي', value: 'disciplines' },
              { label: 'التغلب على الخوف والقلق', value: 'fear' },
              { label: 'العثور على الهدف والدعوة', value: 'purpose' },
              { label: 'تحسين العلاقات', value: 'relationships' }
            ],
            helperText: 'اختر جميع المجالات التي تنطبق على رحلتك الروحية'
          }
        ]
      }
    ],
    submitButtonText: 'التسجيل في الخلوة',
    successMessage: 'تم إكمال تسجيلك في الخلوة! سنرسل لك معلومات مفصلة قريبًا.',
    errorMessage: 'فشل التسجيل. يرجى المحاولة مرة أخرى.'
  },
  workshop: {
    id: 'workshop-registration',
    title: 'تسجيل الورشة',
    description: 'سجل في ورشة الخدمة العملية',
    sections: [
      {
        id: 'basic-info',
        title: 'المعلومات الأساسية',
        order: 1,
        fields: [
          {
            id: 'fullName',
            name: 'fullName',
            label: 'الاسم الكامل',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسمك الكامل',
            required: true,
            validationRules: [ValidationRuleFactory.required()]
          },
          {
            id: 'email',
            name: 'email',
            label: 'عنوان البريد الإلكتروني',
            type: FormFieldType.EMAIL,
            placeholder: 'بريدك الإلكتروني@example.com',
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.email()]
          },
          {
            id: 'phone',
            name: 'phone',
            label: 'رقم الهاتف',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [ValidationRuleFactory.required(), ValidationRuleFactory.phone()]
          },
          {
            id: 'workshopTrack',
            name: 'workshopTrack',
            label: 'مسار الورشة',
            type: FormFieldType.SELECT,
            options: [
              { label: 'اختر مسارًا', value: '' },
              { label: 'مبتدئ - جديد في الخدمة', value: 'beginner' },
              { label: 'متوسط - بعض الخبرة', value: 'intermediate' },
              { label: 'متقدم - مستوى القيادة', value: 'advanced' }
            ],
            required: true,
            validationRules: [ValidationRuleFactory.required('يرجى تحديد مسار الورشة')]
          }
        ]
      },
      {
        id: 'experience-goals',
        title: 'الخبرة والأهداف',
        order: 2,
        fields: [
          {
            id: 'currentRole',
            name: 'currentRole',
            label: 'دورك الحالي في الخدمة (إن وجد)',
            type: FormFieldType.TEXT,
            placeholder: 'مثال: قائد شباب، متطوع، راعي، إلخ.'
          },
          {
            id: 'learningGoals',
            name: 'learningGoals',
            label: 'ما الذي تأمل في تعلمه من هذه الورشة؟',
            type: FormFieldType.TEXTAREA,
            placeholder: 'شارك أهداف التعلم الخاصة بك وما تأمل في اكتسابه من المهارات والمعرفة...',
            required: true,
            validationRules: [ValidationRuleFactory.required('يرجى مشاركة أهداف التعلم الخاصة بك')]
          }
        ]
      }
    ],
    submitButtonText: 'التسجيل في الورشة',
    successMessage: 'تم إكمال تسجيل الورشة! تحقق من بريدك الإلكتروني للحصول على المواد والتفاصيل.',
    errorMessage: 'فشل التسجيل. يرجى المحاولة مرة أخرى.'
  },
  program: {
    id: '7weeks-enrollment',
    title: 'تسجيل برنامج 7 أسابيع',
    description: 'انضم إلى رحلتنا التحويلية الروحية لمدة 7 أسابيع',
    sections: [
      {
        id: 'personal-information',
        title: 'معلومات شخصية',
        description: 'أخبرنا عن نفسك',
        order: 1,
        fields: [
          {
            id: 'firstName',
            name: 'firstName',
            label: 'الاسم الأول',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسمك الأول',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.minLength(2, 'يجب أن يكون الاسم الأول مكونًا من حرفين على الأقل')
            ]
          },
          {
            id: 'lastName',
            name: 'lastName',
            label: 'اسم العائلة',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسم عائلتك',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.minLength(2, 'يجب أن يكون اسم العائلة مكونًا من حرفين على الأقل')
            ]
          },
          {
            id: 'email',
            name: 'email',
            label: 'عنوان البريد الإلكتروني',
            type: FormFieldType.EMAIL,
            placeholder: 'بريدك الإلكتروني@example.com',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.email()
            ]
          },
          {
            id: 'mobile',
            name: 'mobile',
            label: 'رقم الجوال',
            type: FormFieldType.PHONE,
            placeholder: '+1 (555) 123-4567',
            required: true,
            validationRules: [
              ValidationRuleFactory.required(),
              ValidationRuleFactory.phone()
            ]
          },
          {
            id: 'age',
            name: 'age',
            label: 'العمر',
            type: FormFieldType.NUMBER,
            placeholder: 'أدخل عمرك',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى إدخال عمرك'),
              ValidationRuleFactory.custom(
                (value) => value >= 18 && value <= 80,
                'يجب أن يكون العمر بين 18 و 80 عامًا'
              )
            ]
          },
          {
            id: 'gender',
            name: 'gender',
            label: 'الجنس',
            type: FormFieldType.RADIO,
            options: [
              { label: 'ذكر', value: 'male' },
              { label: 'أنثى', value: 'female' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد جنسك')
            ]
          }
        ]
      },
      {
        id: 'spiritual-background',
        title: 'الخلفية الروحية',
        description: 'ساعدنا على فهم رحلتك المسيحية',
        order: 2,
        fields: [
          {
            id: 'christianityBackground',
            name: 'christianityBackground',
            label: 'خلفيتك في المسيحية',
            type: FormFieldType.SELECT,
            options: [
              { label: 'اختر خلفيتك', value: '' },
              { label: 'مؤمن جديد (أقل من سنة واحدة)', value: 'new-believer' },
              { label: 'مسيحي نامٍ (1-5 سنوات)', value: 'growing' },
              { label: 'مؤمن ناضج (أكثر من 5 سنوات)', value: 'mature' },
              { label: 'مسيحي ولد من جديد منذ الطفولة', value: 'childhood' },
              { label: 'عاد مؤخرًا إلى الإيمان', value: 'returned' },
              { label: 'يستكشف المسيحية', value: 'exploring' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد خلفيتك في المسيحية')
            ]
          },
          {
            id: 'churchAttendance',
            name: 'churchAttendance',
            label: 'حضور الكنيسة',
            type: FormFieldType.RADIO,
            options: [
              { label: 'حاضر بانتظام (أسبوعيًا)', value: 'regular' },
              { label: 'حاضر أحيانًا (شهريًا)', value: 'occasional' },
              { label: 'حاضر نادرًا (في المناسبات الخاصة)', value: 'rare' },
              { label: 'تجربة كنيسة لأول مرة', value: 'first-time' },
              { label: 'كنيسة عبر الإنترنت فقط', value: 'online-only' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد نمط حضورك للكنيسة')
            ]
          },
          {
            id: 'baptismStatus',
            name: 'baptismStatus',
            label: 'حالة المعمودية',
            type: FormFieldType.RADIO,
            options: [
              { label: 'نعم، لقد تم تعميدي', value: 'baptized' },
              { label: 'لا، لكنني مهتم', value: 'interested' },
              { label: 'لا، وأحتاج إلى مزيد من المعلومات', value: 'need-info' },
              { label: 'أفضل عدم الإجابة', value: 'prefer-not' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد حالة تعميدك')
            ]
          },
          {
            id: 'currentChurch',
            name: 'currentChurch',
            label: 'الكنيسة الحالية (إن وجدت)',
            type: FormFieldType.TEXT,
            placeholder: 'أدخل اسم كنيستك الحالية',
            helperText: 'اتركه فارغًا إذا كنت لا تحضر حاليًا كنيسة'
          }
        ]
      },
      {
        id: 'program-specific',
        title: 'معلومات البرنامج',
        description: 'تفاصيل خاصة ببرنامج حصاد 7 أسابيع',
        order: 3,
        fields: [
          {
            id: 'healingAreas',
            name: 'healingAreas',
            label: 'المجالات التي تبحث فيها عن الشفاء أو النمو',
            type: FormFieldType.MULTI_SELECT,
            options: [
              { label: 'الشفاء العاطفي من الصدمات السابقة', value: 'emotional-trauma' },
              { label: 'العبودية الروحية والحرية', value: 'spiritual-bondage' },
              { label: 'استعادة العلاقات', value: 'relationships' },
              { label: 'الشفاء الجسدي والعافية', value: 'physical-healing' },
              { label: 'التغلب على الإدمان', value: 'addiction' },
              { label: 'الاكتئاب والقلق', value: 'mental-health' },
              { label: 'المغفرة (إعطاء واستقبال)', value: 'forgiveness' },
              { label: 'فهم محبة الله وغرضه', value: 'gods-love' },
              { label: 'كسر الأنماط الجينية', value: 'generational' },
              { label: 'الاختراق المالي والإدارة', value: 'financial' }
            ],
            helperText: 'اختر جميع المجالات التي تنطبق على رحلتك'
          },
          {
            id: 'commitmentLevel',
            name: 'commitmentLevel',
            label: 'التزامك ببرنامج الـ 7 أسابيع',
            type: FormFieldType.RADIO,
            options: [
              { label: 'يمكنني حضور جميع الجلسات السبع بانتظام', value: 'full-commitment' },
              { label: 'يمكنني حضور معظم الجلسات (5-6 جلسات)', value: 'mostly-committed' },
              { label: 'سأبذل قصارى جهدي ولكن قد أفوت 2-3 جلسات', value: 'partial-commitment' },
              { label: 'يمكنني الحضور فقط في بعض الأحيان', value: 'occasional' }
            ],
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى تحديد مستوى التزامك')
            ]
          },
          {
            id: 'motivation',
            name: 'motivation',
            label: 'ما الذي يجذبك إلى هذا البرنامج الذي يستمر لمدة 7 أسابيع؟',
            type: FormFieldType.TEXTAREA,
            placeholder: 'شارك قلبك وما تأمل في اكتسابه من هذه الرحلة التحويلية مع المسيح...',
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يرجى مشاركة دوافعك للانضمام'),
              ValidationRuleFactory.minLength(30, 'يرجى تقديم مزيد من التفاصيل حول دوافعك (حد أدنى 30 حرفًا)')
            ]
          },
          {
            id: 'prayerSupport',
            name: 'prayerSupport',
            label: 'هل لديك نظام دعم للصلاة؟',
            type: FormFieldType.RADIO,
            options: [
              { label: 'نعم، لدي دعم قوي للصلاة', value: 'strong-support' },
              { label: 'نعم، ولكن الدعم محدود', value: 'limited-support' },
              { label: 'لا، أحتاج إلى دعم للصلاة', value: 'need-support' },
              { label: 'لست متأكدًا مما يعنيه هذا', value: 'unsure' }
            ],
            helperText: 'يساعدنا هذا في فهم كيفية دعمك بشكل أفضل خلال البرنامج'
          }
        ]
      },
      {
        id: 'agreement-commitment',
        title: 'اتفاقية البرنامج',
        description: 'يرجى قراءة وتأكيد فهمك',
        order: 4,
        fields: [
          {
            id: 'programUnderstanding',
            name: 'programUnderstanding',
            label: 'أفهم أن هذا برنامج روحي مكثف يستمر لمدة 7 أسابيع ويتطلب الالتزام والصلاة والانفتاح على عمل الله الشافي في حياتي',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('يجب عليك إقرار فهم متطلبات البرنامج')
            ]
          },
          {
            id: 'prayerConsent',
            name: 'prayerConsent',
            label: 'أوافق على تلقي خدمة الصلاة وأفهم أن هذا البرنامج يتضمن إرشادًا روحيًا قائمًا على المبادئ الكتابية',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('موافقة خدمة الصلاة مطلوبة للمشاركة')
            ]
          },
          {
            id: 'confidentialityAgreement',
            name: 'confidentialityAgreement',
            label: 'أوافق على الحفاظ على سرية الشهادات الشخصية والتجارب التي يتم مشاركتها خلال جلسات المجموعة',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('اتفاقية السرية مطلوبة')
            ]
          },
          {
            id: 'communicationConsent',
            name: 'communicationConsent',
            label: 'أوافق على أن يتم الاتصال بي من قبل كنيسة زوي بخصوص هذا البرنامج، ورعاية المتابعة، وفرص النمو الروحي ذات الصلة',
            type: FormFieldType.CHECKBOX,
            required: true,
            validationRules: [
              ValidationRuleFactory.required('موافقة الاتصال مطلوبة')
            ]
          }
        ]
      }
    ],
    submitButtonText: 'التسجيل في برنامج 7 أسابيع',
    cancelButtonText: 'إلغاء',
    successMessage: 'الحمد لله! تم تقديم تسجيلك في برنامج حصاد 7 أسابيع. سيتصل بك فريق الخدمة لدينا خلال 24 ساعة مع الخطوات التالية ومواد التحضير.',
    errorMessage: 'حدث خطأ أثناء معالجة تسجيلك. يرجى المحاولة مرة أخرى أو الاتصال بمكتب خدمتنا للحصول على المساعدة.',
    externalUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSdXkNmQfBgJHRqNzStwVkEHgQCZhJQZyPbxDctR8Sm3WqYfow/viewform?usp=header'
  }
};

export const getFormByType = (formType: string = 'conference', language: Language = Language.EN): FormConfiguration => {
  const forms = language === Language.AR ? COMMON_FORM_MOCK_DATA_AR : COMMON_FORM_MOCK_DATA_EN;
  return forms[formType] || forms.conference;
};